// src/pages/PromoPage.js — FIXED (handles API errors, shows loading state)
import React, { useState, useEffect } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const EMPTY = { code: '', type: 'PERCENT', value: '', minOrder: '', maxUses: '100', expiresAt: '' };

// Mock data shown when API not available
const MOCK_PROMOS = [
  { id: 'mock-1', code: 'SAVE10', type: 'PERCENT', value: 10, minOrder: 0,  maxUses: 100, usedCount: 5,  isActive: true,  expiresAt: null },
  { id: 'mock-2', code: 'FLAT5',  type: 'FLAT',    value: 5,  minOrder: 20, maxUses: 50,  usedCount: 12, isActive: false, expiresAt: null },
];

export default function PromoPage() {
  const { vendor }  = useAuth();
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [promos,    setPromos]    = useState([]);
  const [form,      setForm]      = useState(EMPTY);
  const [adding,    setAdding]    = useState(false);
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [msg,       setMsg]       = useState({ text: '', type: '' });

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 3000);
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/promos');
      setPromos(res.data || []);
    } catch (err) {
      console.warn('Promos API not available, using mock data:', err.message);
      setPromos(MOCK_PROMOS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.code.trim()) return showMsg('Code is required', 'error');
    if (!form.value)       return showMsg('Value is required', 'error');

    setSaving(true);
    try {
      await api.post('/promos', {
        ...form,
        value:    Number(form.value),
        minOrder: Number(form.minOrder || 0),
        maxUses:  Number(form.maxUses  || 100),
        expiresAt: form.expiresAt || null,
      });
      showMsg('✅ Promo code created!');
      setForm(EMPTY);
      setAdding(false);
      load();
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.error || 'Failed to create code'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await api.patch(`/promos/${id}/toggle`);
      setPromos((prev) => prev.map((p) => p.id === id ? { ...p, isActive: !p.isActive } : p));
    } catch (_) {
      showMsg('❌ Failed to update', 'error');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/promos/${id}`);
      setPromos((prev) => prev.filter((p) => p.id !== id));
      showMsg('✅ Deleted');
    } catch (_) {
      showMsg('❌ Failed to delete', 'error');
    }
  };

  return (
    <div className="page">
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div className="page-title">🎟 Promo Codes</div>
          <div className="page-sub">Create and manage discount codes for your customers</div>
        </div>
        <button className="btn btn-primary" onClick={() => setAdding(!adding)}>
          {adding ? 'Cancel' : '+ New Code'}
        </button>
      </div>

      {/* Message bar */}
      {msg.text && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.type === 'error' ? '#3d1a1a' : '#1a3d2a',
          color:      msg.type === 'error' ? 'var(--red)' : 'var(--green)',
          border:     `1px solid ${msg.type === 'error' ? 'var(--red)' : 'var(--green)'}`,
        }}>
          {msg.text}
        </div>
      )}

      {/* Create form */}
      {adding && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>New Promo Code</div>
          <div className="grid2" style={{ gap: 12 }}>

            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Code *</div>
              <input
                type="text"
                placeholder="e.g. SAVE20"
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                style={{ fontFamily: 'monospace', letterSpacing: 2, textTransform: 'uppercase' }}
              />
            </div>

            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Discount Type</div>
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="PERCENT">Percentage (e.g. 10 = 10% off)</option>
                <option value="FLAT">Flat amount (e.g. 5 = AED 5 off)</option>
              </select>
            </div>

            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>
                Value * {form.type === 'PERCENT' ? '(%)' : '(AED)'}
              </div>
              <input
                type="number"
                placeholder={form.type === 'PERCENT' ? '10' : '5'}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                min="0"
              />
            </div>

            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Min Order (AED)</div>
              <input
                type="number"
                placeholder="0 = no minimum"
                value={form.minOrder}
                onChange={(e) => setForm({ ...form, minOrder: e.target.value })}
                min="0"
              />
            </div>

            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Max Uses</div>
              <input
                type="number"
                placeholder="100"
                value={form.maxUses}
                onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
                min="1"
              />
            </div>

            <div>
              <div className="section-label" style={{ marginBottom: 6 }}>Expiry Date (optional)</div>
              <input
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          {/* Preview */}
          {form.code && form.value && (
            <div style={{
              marginTop: 16, padding: '10px 14px',
              background: '#3d2e10', borderRadius: 8,
              fontSize: 13, color: 'var(--accent)',
            }}>
              Preview: Code <strong>{form.code}</strong> gives{' '}
              {form.type === 'PERCENT' ? `${form.value}% off` : `AED ${form.value} off`}
              {form.minOrder > 0 ? ` on orders over AED ${form.minOrder}` : ''}
              {form.maxUses ? `, max ${form.maxUses} uses` : ''}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create Code'}
            </button>
            <button className="btn btn-ghost" onClick={() => { setAdding(false); setForm(EMPTY); }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Promo list */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{
          padding: '14px 16px', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontWeight: 700 }}>Your Promo Codes ({promos.length})</span>
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>
            {promos.filter((p) => p.isActive).length} active
          </span>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
            Loading promo codes...
          </div>
        )}

        {!loading && promos.length === 0 && (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
            No promo codes yet. Create your first one above.
          </div>
        )}

        {!loading && promos.map((p) => (
          <div key={p.id} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', borderBottom: '1px solid var(--border)',
          }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <span style={{
                  fontFamily: 'monospace', fontWeight: 700, fontSize: 16,
                  color: 'var(--accent)', letterSpacing: 1,
                }}>
                  {p.code}
                </span>
                <span className={`badge ${p.isActive ? 'badge-green' : 'badge-red'}`}>
                  {p.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                {p.type === 'PERCENT' ? `${p.value}% off` : `AED ${p.value} off`}
                {p.minOrder > 0  && ` · Min AED ${p.minOrder}`}
                {` · ${p.usedCount}/${p.maxUses} uses`}
                {p.expiresAt && ` · Expires ${new Date(p.expiresAt).toLocaleDateString()}`}
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div
                className={`toggle${p.isActive ? ' on' : ''}`}
                onClick={() => handleToggle(p.id)}
                style={{ cursor: 'pointer' }}
              />
              <button
                className="btn btn-danger btn-sm"
                onClick={() => setConfirm({ message: 'Delete this promo code?', confirmLabel: 'Delete', onConfirm: () => handleDelete(p.id) })}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Usage stats */}
      {promos.length > 0 && (
        <div className="grid3" style={{ marginTop: 16 }}>
          {[
            ['Total Codes',    promos.length],
            ['Active',         promos.filter((p) => p.isActive).length],
            ['Total Uses',     promos.reduce((s, p) => s + (p.usedCount || 0), 0)],
          ].map(([label, val]) => (
            <div key={label} className="stat-card">
              <div className="stat-label">{label}</div>
              <div className="stat-val">{val}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
